import { useEffect, useMemo, useState } from 'react';
import App from './App';
import { getSupabase } from './supabaseClient';
import {
  buildSpecialistImagePath,
  createSpecialistDraft,
  DEFAULT_SPECIALISTS,
  fetchSpecialistRows,
  mergeSpecialistsWithRows,
  slugifySpecialistId,
  SPECIALIST_IMAGE_ACCEPT,
  SPECIALIST_IMAGE_BUCKET,
  SPECIALIST_IMAGE_MAX_BYTES
} from './specialists';

const ADMIN_SESSION_KEY = 'chatai_admin_session';
const ADMIN_EMAIL = 'admin@chatai.app';
const ADMIN_PASSWORD = 'Admin@12345';
const ADMIN_NAME = 'Site Admin';

const Icon = ({ path, size = 18 }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    focusable="false"
  >
    {path}
  </svg>
);

const ICONS = {
  overview: (
    <Icon
      path={
        <>
          <rect x="3" y="3" width="8" height="8" rx="1.5" />
          <rect x="13" y="3" width="8" height="5" rx="1.5" />
          <rect x="13" y="10" width="8" height="11" rx="1.5" />
          <rect x="3" y="13" width="8" height="8" rx="1.5" />
        </>
      }
    />
  ),
  users: (
    <Icon
      path={
        <>
          <circle cx="9" cy="8" r="3.2" />
          <path d="M3 20c1.2-3.5 4-5 6-5s4.8 1.5 6 5" />
          <circle cx="17" cy="9" r="2.4" />
          <path d="M15.5 14.5c2 0 4 1.2 5 3" />
        </>
      }
    />
  ),
  sessions: (
    <Icon
      path={
        <>
          <path d="M4 5h16v11H7l-3 3z" />
          <path d="M8 9h8M8 12h5" />
        </>
      }
    />
  ),
  chat: (
    <Icon
      path={
        <>
          <path d="M12 20c4.4 0 8-3.1 8-7s-3.6-7-8-7-8 3.1-8 7c0 1.8.8 3.4 2.1 4.7L5 21l3.4-1.6c1.1.4 2.3.6 3.6.6z" />
        </>
      }
    />
  ),
  messages: (
    <Icon
      path={
        <>
          <path d="M21 12a8 8 0 0 1-11.6 7.1L4 21l1.9-5.4A8 8 0 1 1 21 12z" />
        </>
      }
    />
  ),
  learnings: (
    <Icon
      path={
        <>
          <path d="M4 5a2 2 0 0 1 2-2h11l3 3v15a1 1 0 0 1-1 1H6a2 2 0 0 1-2-2z" />
          <path d="M8 8h8M8 12h8M8 16h5" />
        </>
      }
    />
  ),
  memories: (
    <Icon
      path={
        <>
          <path d="M12 21s-7-4.5-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 11c0 5.5-7 10-7 10z" />
        </>
      }
    />
  ),
  bookings: (
    <Icon
      path={
        <>
          <rect x="4" y="5" width="16" height="15" rx="2" />
          <path d="M8 3v4M16 3v4M4 10h16" />
          <path d="M8 14h3M8 17h6" />
        </>
      }
    />
  ),
  specialists: (
    <Icon
      path={
        <>
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <circle cx="9" cy="10" r="1.8" />
          <path d="M21 16l-4.8-4.8a1 1 0 0 0-1.4 0L8 18" />
        </>
      }
    />
  ),
  settings: (
    <Icon
      path={
        <>
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-1.8-.3 1.6 1.6 0 0 0-1 1.5V21a2 2 0 0 1-4 0v-.1a1.6 1.6 0 0 0-1-1.5 1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0 .3-1.8 1.6 1.6 0 0 0-1.5-1H3a2 2 0 0 1 0-4h.1a1.6 1.6 0 0 0 1.5-1 1.6 1.6 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3h0a1.6 1.6 0 0 0 1-1.5V3a2 2 0 0 1 4 0v.1a1.6 1.6 0 0 0 1 1.5 1.6 1.6 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8v0a1.6 1.6 0 0 0 1.5 1H21a2 2 0 0 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1z" />
        </>
      }
    />
  )
};

const TABS = [
  { id: 'overview', label: 'Overview', icon: ICONS.overview },
  { id: 'chat', label: 'Chat', icon: ICONS.chat },
  { id: 'users', label: 'Users', icon: ICONS.users },
  { id: 'sessions', label: 'Chat sessions', icon: ICONS.sessions },
  { id: 'messages', label: 'Messages', icon: ICONS.messages },
  { id: 'specialists', label: 'Specialists', icon: ICONS.specialists },
  { id: 'bookings', label: 'Bookings', icon: ICONS.bookings },
  { id: 'learnings', label: 'Learnings', icon: ICONS.learnings },
  { id: 'memories', label: 'User memories', icon: ICONS.memories },
  { id: 'settings', label: 'Settings', icon: ICONS.settings }
];

const HIDDEN_FROM_SIDEBAR = new Set(['sessions', 'messages', 'memories']);
const SIDEBAR_TABS = TABS.filter((tab) => !HIDDEN_FROM_SIDEBAR.has(tab.id));
const TAB_IDS = TABS.map((t) => t.id);

const getTabFromPath = () => {
  const path = window.location.pathname.replace(/\/+$/, '').toLowerCase();
  if (path === '/admin' || path === '') return 'overview';
  const match = path.match(/^\/admin\/([^/]+)/);
  const slug = match ? match[1] : '';
  return TAB_IDS.includes(slug) ? slug : 'overview';
};

const pathForTab = (tabId) => (tabId === 'overview' ? '/admin' : `/admin/${tabId}`);

function AdminLogin({ onSignedIn }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submit = async () => {
    const trimmedEmail = email.trim().toLowerCase();
    const trimmedPassword = password.trim();
    if (!trimmedEmail || !trimmedPassword) {
      setError('Please enter an email and password.');
      return;
    }
    setIsSubmitting(true);
    setError('');
    if (
      trimmedEmail !== ADMIN_EMAIL.toLowerCase() ||
      trimmedPassword !== ADMIN_PASSWORD
    ) {
      setError('Invalid admin email or password.');
      setIsSubmitting(false);
      return;
    }
    const adminData = {
      id: 'local-admin',
      name: ADMIN_NAME,
      email: ADMIN_EMAIL
    };
    window.localStorage.setItem(ADMIN_SESSION_KEY, JSON.stringify(adminData));
    onSignedIn(adminData);
    setIsSubmitting(false);
  };

  return (
    <div className="auth-shell">
      <section className="auth-card">
        <p className="section-label">Admin</p>
        <h1>Admin sign in</h1>
        <p className="auth-subtitle">
          Restricted area. Use your administrator credentials.
        </p>

        <label className="auth-label" htmlFor="admin-email">
          Email
        </label>
        <input
          id="admin-email"
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          autoComplete="email"
          className="auth-input"
          placeholder="admin@example.com"
        />

        <label className="auth-label" htmlFor="admin-password">
          Password
        </label>
        <input
          id="admin-password"
          type="password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          autoComplete="current-password"
          className="auth-input"
          placeholder="Enter password"
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !isSubmitting) {
              event.preventDefault();
              void submit();
            }
          }}
        />

        {error && <p className="auth-error">{error}</p>}

        <button
          type="button"
          className="new-session-button auth-submit"
          onClick={() => void submit()}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Signing in...' : 'Sign in as admin'}
        </button>
      </section>
    </div>
  );
}

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
};

const truncate = (text, max = 90) => {
  if (!text) return '';
  const t = String(text).replace(/\s+/g, ' ').trim();
  return t.length > max ? `${t.slice(0, max)}…` : t;
};

function useSupabaseRows({ table, select, order = 'created_at', limit = 100, deps = [] }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setError('');
    (async () => {
      try {
        const { data, error: queryError } = await getSupabase()
          .from(table)
          .select(select)
          .order(order, { ascending: false })
          .limit(limit);
        if (cancelled) return;
        if (queryError) {
          setError(queryError.message);
          setRows([]);
          return;
        }
        setRows(data ?? []);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
          setRows([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [table, select, order, limit, reloadKey, ...deps]); // eslint-disable-line react-hooks/exhaustive-deps

  return { rows, error, reload: () => setReloadKey((n) => n + 1) };
}

const formatRelative = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const diff = Date.now() - d.getTime();
  const min = Math.round(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

function PageHeader({ title, subtitle, onReload }) {
  return (
    <header className="admin-page__header">
      <div>
        <h2 className="admin-page__title">{title}</h2>
        {subtitle && <p className="admin-page__subtitle">{subtitle}</p>}
      </div>
      {onReload && (
        <button type="button" className="admin-reload" onClick={onReload}>
          Refresh
        </button>
      )}
    </header>
  );
}

function StatusLine({ rows, error, emptyText = 'No rows yet.' }) {
  if (rows === null) {
    return <p className="admin-status">Loading…</p>;
  }
  if (error) {
    return <p className="auth-error">{error}</p>;
  }
  if (!rows.length) {
    return <p className="admin-status">{emptyText}</p>;
  }
  return null;
}

const normalizeAdminChatUser = (row) => {
  if (!row) {
    return null;
  }
  return {
    id: row.id,
    name: row.name,
    email: row.email,
    plan_tier: row.plan_tier === 'pro' ? 'pro' : 'free'
  };
};

const isMissingPlanTierColumnError = (error) => {
  const message =
    error && typeof error.message === 'string' ? error.message.toLowerCase() : '';
  return message.includes('plan_tier') && message.includes('column');
};

const fetchMaybeSingleAdminChatUser = async (email) => {
  const supabase = getSupabase();
  const runQuery = (includePlanTier) =>
    supabase
      .from('app_users')
      .select(includePlanTier ? 'id, name, email, plan_tier' : 'id, name, email')
      .eq('email', email)
      .maybeSingle();

  let result = await runQuery(true);
  if (result.error && isMissingPlanTierColumnError(result.error)) {
    result = await runQuery(false);
  }

  return {
    ...result,
    data: normalizeAdminChatUser(result.data)
  };
};

const insertAdminChatUser = async ({ name, email, password }) => {
  const supabase = getSupabase();
  const insertPayload = {
    name,
    email,
    password
  };

  let result = await supabase
    .from('app_users')
    .insert(insertPayload)
    .select('id, name, email, plan_tier')
    .single();

  if (result.error && isMissingPlanTierColumnError(result.error)) {
    result = await supabase
      .from('app_users')
      .insert(insertPayload)
      .select('id, name, email')
      .single();
  }

  return {
    ...result,
    data: normalizeAdminChatUser(result.data)
  };
};

async function ensureAdminChatUser(admin) {
  const email = admin.email.trim().toLowerCase();
  const name = admin.name?.trim() || ADMIN_NAME;

  const existing = await fetchMaybeSingleAdminChatUser(email);
  if (existing.error) {
    throw existing.error;
  }
  if (existing.data) {
    return existing.data;
  }

  const inserted = await insertAdminChatUser({
    name,
    email,
    password: ADMIN_PASSWORD
  });

  if (!inserted.error) {
    return inserted.data;
  }

  const errorMessage =
    inserted.error && typeof inserted.error.message === 'string'
      ? inserted.error.message.toLowerCase()
      : '';
  if (errorMessage.includes('duplicate key')) {
    const retry = await fetchMaybeSingleAdminChatUser(email);
    if (retry.error) {
      throw retry.error;
    }
    if (retry.data) {
      return retry.data;
    }
  }

  throw inserted.error;
}

function AdminChatPage({ admin, onSignOut }) {
  const [chatUser, setChatUser] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const resolvedUser = await ensureAdminChatUser(admin);
        if (!cancelled) {
          setChatUser(resolvedUser);
          setError('');
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : String(err));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [admin]);

  if (error) {
    return (
      <div className="admin-page">
        <PageHeader
          title="Chat"
          subtitle="The same chatbot experience as the customer app, now available for admin use."
        />
        <p className="auth-error">{error}</p>
      </div>
    );
  }

  if (!chatUser) {
    return (
      <div className="admin-page">
        <PageHeader
          title="Chat"
          subtitle="The same chatbot experience as the customer app, now available for admin use."
        />
        <p className="admin-status">Preparing the admin chat workspace...</p>
      </div>
    );
  }

  return (
    <div className="admin-chat-page">
      <App
        basePath="/admin/chat"
        externalAuthSession={chatUser}
        onExternalSignOut={onSignOut}
        allowUnlimitedChat
        embeddedMode
      />
    </div>
  );
}

function OverviewPage({ admin, onNavigate }) {
  const [stats, setStats] = useState({
    users: null,
    sessions: null,
    messages: null,
    bookings: null,
    learnings: null,
    memories: null,
    usersLast7: null,
    messagesLast24: null,
    sessionsLast7: null,
    bookingsLast7: null
  });
  const [statsError, setStatsError] = useState('');
  const [recentUsers, setRecentUsers] = useState(null);
  const [recentMessages, setRecentMessages] = useState(null);
  const [recentBookings, setRecentBookings] = useState(null);
  const [journeyCounts, setJourneyCounts] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const supabase = getSupabase();
        const sinceDay = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
        const sinceWeek = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();

        const queries = await Promise.all([
          supabase.from('app_users').select('id', { count: 'exact', head: true }),
          supabase.from('chat_sessions').select('id', { count: 'exact', head: true }),
          supabase.from('chat_messages').select('id', { count: 'exact', head: true }),
          supabase
            .from('specialist_bookings')
            .select('id', { count: 'exact', head: true }),
          supabase.from('chat_learnings').select('id', { count: 'exact', head: true }),
          supabase.from('user_memories').select('id', { count: 'exact', head: true }),
          supabase
            .from('app_users')
            .select('id', { count: 'exact', head: true })
            .gte('created_at', sinceWeek),
          supabase
            .from('chat_messages')
            .select('id', { count: 'exact', head: true })
            .gte('created_at', sinceDay),
          supabase
            .from('chat_sessions')
            .select('id', { count: 'exact', head: true })
            .gte('created_at', sinceWeek),
          supabase
            .from('specialist_bookings')
            .select('id', { count: 'exact', head: true })
            .gte('created_at', sinceWeek),
          supabase
            .from('app_users')
            .select('id, name, email, created_at')
            .order('created_at', { ascending: false })
            .limit(5),
          supabase
            .from('chat_messages')
            .select('id, session_id, role, type, content, created_at')
            .order('created_at', { ascending: false })
            .limit(5),
          supabase
            .from('specialist_bookings')
            .select('id, specialist_name, full_name, email, created_at')
            .order('created_at', { ascending: false })
            .limit(5),
          supabase
            .from('chat_sessions')
            .select('journey_id')
            .limit(1000)
        ]);

        if (cancelled) return;

        const [
          usersRes,
          sessionsRes,
          messagesRes,
          bookingsRes,
          learningsRes,
          memoriesRes,
          usersWeekRes,
          messagesDayRes,
          sessionsWeekRes,
          bookingsWeekRes,
          recentUsersRes,
          recentMessagesRes,
          recentBookingsRes,
          journeysRes
        ] = queries;

        const firstError =
          usersRes.error ||
          sessionsRes.error ||
          messagesRes.error ||
          bookingsRes.error ||
          learningsRes.error ||
          memoriesRes.error;
        if (firstError) setStatsError(firstError.message);

        setStats({
          users: usersRes.count ?? null,
          sessions: sessionsRes.count ?? null,
          messages: messagesRes.count ?? null,
          bookings: bookingsRes.count ?? null,
          learnings: learningsRes.count ?? null,
          memories: memoriesRes.count ?? null,
          usersLast7: usersWeekRes.count ?? null,
          messagesLast24: messagesDayRes.count ?? null,
          sessionsLast7: sessionsWeekRes.count ?? null,
          bookingsLast7: bookingsWeekRes.count ?? null
        });

        setRecentUsers(recentUsersRes.error ? [] : recentUsersRes.data ?? []);
        setRecentMessages(recentMessagesRes.error ? [] : recentMessagesRes.data ?? []);
        setRecentBookings(recentBookingsRes.error ? [] : recentBookingsRes.data ?? []);

        const journeyRows = journeysRes.error ? [] : journeysRes.data ?? [];
        const journeyMap = new Map();
        journeyRows.forEach((row) => {
          const key = row.journey_id || 'unknown';
          journeyMap.set(key, (journeyMap.get(key) || 0) + 1);
        });
        const journeyTotal = journeyRows.length || 1;
        const journeyArr = Array.from(journeyMap.entries())
          .map(([id, count]) => ({
            id,
            count,
            pct: Math.round((count / journeyTotal) * 100)
          }))
          .sort((a, b) => b.count - a.count);
        setJourneyCounts(journeyArr);
      } catch (err) {
        if (!cancelled) {
          setStatsError(err instanceof Error ? err.message : String(err));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 5) return 'Hello';
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    if (hour < 21) return 'Good evening';
    return 'Hello';
  }, []);

  const today = useMemo(
    () =>
      new Date().toLocaleDateString([], {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      }),
    []
  );

  const formatStat = (value) =>
    value === null || value === undefined ? '—' : new Intl.NumberFormat().format(value);

  const STAT_CARDS = [
    {
      key: 'users',
      label: 'Registered users',
      value: stats.users,
      hint:
        stats.usersLast7 !== null && stats.usersLast7 !== undefined
          ? `+${stats.usersLast7} this week`
          : 'all time',
      tone: 'violet',
      icon: ICONS.users,
      tab: 'users'
    },
    {
      key: 'sessions',
      label: 'Chat sessions',
      value: stats.sessions,
      hint:
        stats.sessionsLast7 !== null && stats.sessionsLast7 !== undefined
          ? `+${stats.sessionsLast7} this week`
          : 'all time',
      tone: 'amber',
      icon: ICONS.sessions,
      tab: 'sessions'
    },
    {
      key: 'messages',
      label: 'Messages',
      value: stats.messages,
      hint:
        stats.messagesLast24 !== null && stats.messagesLast24 !== undefined
          ? `+${stats.messagesLast24} in last 24h`
          : 'all time',
      tone: 'green',
      icon: ICONS.messages,
      tab: 'messages'
    },
    {
      key: 'bookings',
      label: 'Bookings',
      value: stats.bookings,
      hint:
        stats.bookingsLast7 !== null && stats.bookingsLast7 !== undefined
          ? `+${stats.bookingsLast7} this week`
          : 'consultation requests',
      tone: 'blue',
      icon: ICONS.bookings,
      tab: 'bookings'
    },
    {
      key: 'learnings',
      label: 'Saved learnings',
      value: stats.learnings,
      hint: 'guidance notes',
      tone: 'blue',
      icon: ICONS.learnings,
      tab: 'learnings'
    },
    {
      key: 'memories',
      label: 'User memories',
      value: stats.memories,
      hint: 'cross-session continuity',
      tone: 'rose',
      icon: ICONS.memories,
      tab: 'memories'
    }
  ];

  return (
    <div className="admin-page">
      <section className="admin-hero">
        <div className="admin-hero__main">
          <p className="admin-hero__eyebrow">{today}</p>
          <h2 className="admin-hero__title">
            {greeting}, {admin.name}
          </h2>
          <p className="admin-hero__subtitle">
            Welcome to your ChatAI control room. Here's a quick pulse on what's
            happening across the app — users joining, conversations forming,
            and the memory layer that keeps every chat personal.
          </p>
          <div className="admin-hero__actions">
            <button
              type="button"
              className="admin-hero__btn admin-hero__btn--primary"
              onClick={() => onNavigate?.('users')}
            >
              View users
            </button>
            <button
              type="button"
              className="admin-hero__btn"
              onClick={() => onNavigate?.('messages')}
            >
              Inspect messages
            </button>
            <a
              className="admin-hero__btn admin-hero__btn--ghost"
              href="/"
            >
              Open user app ↗
            </a>
          </div>
        </div>
        <div className="admin-hero__badge" aria-hidden>
          <div className="admin-hero__orb" />
          <p>{admin.email}</p>
        </div>
      </section>

      <div className="admin-stats admin-stats--rich">
        {STAT_CARDS.map((card) => (
          <button
            key={card.key}
            type="button"
            className={`admin-stat admin-stat--clickable admin-stat--${card.tone}`}
            onClick={() => onNavigate?.(card.tab)}
          >
            <span className="admin-stat__icon" aria-hidden>
              {card.icon}
            </span>
            <span className="admin-stat__label">{card.label}</span>
            <span className="admin-stat__value">{formatStat(card.value)}</span>
            <span className="admin-stat__hint">{card.hint}</span>
          </button>
        ))}
      </div>

      <div className="admin-grid admin-grid--two">
        <section className="admin-panel">
          <header className="admin-panel__header">
            <div>
              <h3>Recent users</h3>
              <p>Latest people who joined.</p>
            </div>
            <button
              type="button"
              className="admin-link"
              onClick={() => onNavigate?.('users')}
            >
              View all →
            </button>
          </header>
          {recentUsers === null && <p className="admin-status">Loading…</p>}
          {recentUsers && recentUsers.length === 0 && (
            <p className="admin-status">No users yet.</p>
          )}
          {recentUsers && recentUsers.length > 0 && (
            <ul className="admin-feed">
              {recentUsers.map((user) => (
                <li key={user.id} className="admin-feed__item">
                  <span
                    className="admin-avatar"
                    aria-hidden
                    style={{
                      background: avatarGradient(user.email || user.name || '?')
                    }}
                  >
                    {(user.name || user.email || '?').charAt(0).toUpperCase()}
                  </span>
                  <div className="admin-feed__body">
                    <p className="admin-feed__title">{user.name}</p>
                    <p className="admin-feed__sub">{user.email}</p>
                  </div>
                  <span className="admin-feed__time">
                    {formatRelative(user.created_at)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="admin-panel">
          <header className="admin-panel__header">
            <div>
              <h3>Recent messages</h3>
              <p>Newest reflections across all sessions.</p>
            </div>
            <button
              type="button"
              className="admin-link"
              onClick={() => onNavigate?.('messages')}
            >
              View all →
            </button>
          </header>
          {recentMessages === null && <p className="admin-status">Loading…</p>}
          {recentMessages && recentMessages.length === 0 && (
            <p className="admin-status">No messages yet.</p>
          )}
          {recentMessages && recentMessages.length > 0 && (
            <ul className="admin-feed">
              {recentMessages.map((msg) => (
                <li key={msg.id} className="admin-feed__item">
                  <span
                    className={`admin-pill admin-pill--${msg.role || 'user'}`}
                  >
                    {msg.role}
                  </span>
                  <div className="admin-feed__body">
                    <p className="admin-feed__title">
                      {truncate(msg.content, 100)}
                    </p>
                    <p className="admin-feed__sub">Session #{msg.session_id}</p>
                  </div>
                  <span className="admin-feed__time">
                    {formatRelative(msg.created_at)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <div className="admin-grid admin-grid--two">
        <section className="admin-panel">
          <header className="admin-panel__header">
            <div>
              <h3>Journey breakdown</h3>
              <p>Distribution of sessions across journeys.</p>
            </div>
            <button
              type="button"
              className="admin-link"
              onClick={() => onNavigate?.('sessions')}
            >
              View all →
            </button>
          </header>
          {journeyCounts === null && <p className="admin-status">Loading…</p>}
          {journeyCounts && journeyCounts.length === 0 && (
            <p className="admin-status">No journeys yet.</p>
          )}
          {journeyCounts && journeyCounts.length > 0 && (
            <ul className="admin-bars">
              {journeyCounts.map((row) => (
                <li key={row.id} className="admin-bar">
                  <div className="admin-bar__head">
                    <span className="admin-bar__label">{row.id}</span>
                    <span className="admin-bar__count">
                      {row.count} <span>({row.pct}%)</span>
                    </span>
                  </div>
                  <div className="admin-bar__track">
                    <div
                      className="admin-bar__fill"
                      style={{ width: `${Math.max(4, row.pct)}%` }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="admin-panel">
          <header className="admin-panel__header">
            <div>
              <h3>Recent bookings</h3>
              <p>Newest specialist booking requests.</p>
            </div>
            <button
              type="button"
              className="admin-link"
              onClick={() => onNavigate?.('bookings')}
            >
              View all →
            </button>
          </header>
          {recentBookings === null && <p className="admin-status">Loading…</p>}
          {recentBookings && recentBookings.length === 0 && (
            <p className="admin-status">No bookings yet.</p>
          )}
          {recentBookings && recentBookings.length > 0 && (
            <ul className="admin-feed">
              {recentBookings.map((booking) => (
                <li key={booking.id} className="admin-feed__item">
                  <span className="admin-pill">{booking.specialist_name}</span>
                  <div className="admin-feed__body">
                    <p className="admin-feed__title">{booking.full_name}</p>
                    <p className="admin-feed__sub">{booking.email}</p>
                  </div>
                  <span className="admin-feed__time">
                    {formatRelative(booking.created_at)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="admin-panel">
          <header className="admin-panel__header">
            <div>
              <h3>Quick actions</h3>
              <p>Jump into the parts of the dashboard you need.</p>
            </div>
          </header>
          <div className="admin-quick-grid">
            <button
              type="button"
              className="admin-quick"
              onClick={() => onNavigate?.('users')}
            >
              <span className="admin-quick__icon" aria-hidden>
                {ICONS.users}
              </span>
              <span className="admin-quick__label">Manage users</span>
              <span className="admin-quick__hint">Browse signups</span>
            </button>
            <button
              type="button"
              className="admin-quick"
              onClick={() => onNavigate?.('learnings')}
            >
              <span className="admin-quick__icon" aria-hidden>
                {ICONS.learnings}
              </span>
              <span className="admin-quick__label">Review learnings</span>
              <span className="admin-quick__hint">Guidance notes</span>
            </button>
            <button
              type="button"
              className="admin-quick"
              onClick={() => onNavigate?.('memories')}
            >
              <span className="admin-quick__icon" aria-hidden>
                {ICONS.memories}
              </span>
              <span className="admin-quick__label">User memories</span>
              <span className="admin-quick__hint">Cross-chat memory</span>
            </button>
            <button
              type="button"
              className="admin-quick"
              onClick={() => onNavigate?.('bookings')}
            >
              <span className="admin-quick__icon" aria-hidden>
                {ICONS.bookings}
              </span>
              <span className="admin-quick__label">View bookings</span>
              <span className="admin-quick__hint">Specialist requests</span>
            </button>
            <button
              type="button"
              className="admin-quick"
              onClick={() => onNavigate?.('settings')}
            >
              <span className="admin-quick__icon" aria-hidden>
                {ICONS.settings}
              </span>
              <span className="admin-quick__label">Settings</span>
              <span className="admin-quick__hint">Account & shortcuts</span>
            </button>
          </div>
        </section>
      </div>

      {statsError && (
        <p className="auth-error">Some stats could not load: {statsError}</p>
      )}
    </div>
  );
}

const avatarGradient = (seed) => {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }
  const h1 = hash % 360;
  const h2 = (h1 + 40) % 360;
  return `linear-gradient(135deg, hsl(${h1} 70% 70%), hsl(${h2} 70% 55%))`;
};

function UsersPage() {
  const { rows, error, reload } = useSupabaseRows({
    table: 'app_users',
    select: 'id, name, email, plan_tier, created_at',
    order: 'created_at',
    limit: 200
  });

  return (
    <div className="admin-page">
      <PageHeader
        title="Users"
        subtitle="Everyone who signed up to the chat."
        onReload={reload}
      />
      <StatusLine rows={rows} error={error} emptyText="No users yet." />
      {rows && rows.length > 0 && (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Email</th>
                <th>Plan</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>{row.id}</td>
                  <td>{row.name}</td>
                  <td>{row.email}</td>
                  <td>{row.plan_tier === 'pro' ? 'Pro' : 'Free'}</td>
                  <td>{formatDateTime(row.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SessionsPage() {
  const { rows, error, reload } = useSupabaseRows({
    table: 'chat_sessions',
    select: 'id, user_id, title, journey_id, created_at',
    order: 'created_at',
    limit: 200
  });

  return (
    <div className="admin-page">
      <PageHeader
        title="Chat sessions"
        subtitle="Reflections started by users."
        onReload={reload}
      />
      <StatusLine rows={rows} error={error} emptyText="No chat sessions yet." />
      {rows && rows.length > 0 && (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>User ID</th>
                <th>Title</th>
                <th>Journey</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>{row.id}</td>
                  <td>{row.user_id}</td>
                  <td>{row.title}</td>
                  <td>{row.journey_id}</td>
                  <td>{formatDateTime(row.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MessagesPage() {
  const { rows, error, reload } = useSupabaseRows({
    table: 'chat_messages',
    select: 'id, session_id, role, type, content, created_at',
    order: 'created_at',
    limit: 200
  });

  return (
    <div className="admin-page">
      <PageHeader
        title="Messages"
        subtitle="Most recent 200 messages, newest first."
        onReload={reload}
      />
      <StatusLine rows={rows} error={error} emptyText="No messages yet." />
      {rows && rows.length > 0 && (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Session</th>
                <th>Role</th>
                <th>Type</th>
                <th>Content</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>{row.id}</td>
                  <td>{row.session_id}</td>
                  <td>{row.role}</td>
                  <td>{row.type}</td>
                  <td title={row.content}>{truncate(row.content, 120)}</td>
                  <td>{formatDateTime(row.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function BookingsPage() {
  const { rows, error, reload } = useSupabaseRows({
    table: 'specialist_bookings',
    select:
      'id, user_id, specialist_id, specialist_name, full_name, email, phone, preferred_date, preferred_time, notes, created_at',
    order: 'created_at',
    limit: 300
  });

  return (
    <div className="admin-page">
      <PageHeader
        title="Bookings"
        subtitle="Requests submitted from the specialist booking form."
        onReload={reload}
      />
      <StatusLine rows={rows} error={error} emptyText="No bookings yet." />
      {rows && rows.length > 0 && (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Specialist</th>
                <th>User ID</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Preferred date</th>
                <th>Preferred time</th>
                <th>Notes</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>{row.id}</td>
                  <td>{row.specialist_name}</td>
                  <td>{row.user_id}</td>
                  <td>{row.full_name}</td>
                  <td>{row.email}</td>
                  <td>{row.phone || '—'}</td>
                  <td>{row.preferred_date || '—'}</td>
                  <td>{row.preferred_time || '—'}</td>
                  <td title={row.notes}>{truncate(row.notes, 100) || '—'}</td>
                  <td>{formatDateTime(row.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const formatSpecialistImageError = (err) => {
  const msg = err instanceof Error ? err.message : String(err);
  const lower = msg.toLowerCase();

  if (lower.includes('specialists') && lower.includes('relation')) {
    return 'Specialists table not found. Run the new SQL migration first.';
  }
  if (lower.includes('bucket') && lower.includes(SPECIALIST_IMAGE_BUCKET)) {
    return 'Specialist images bucket not found. Run the new SQL migration first.';
  }
  if (lower.includes('duplicate key')) {
    return 'A healer with that generated ID already exists. Change the name slightly and try again.';
  }
  if (lower.includes('mime') || lower.includes('content type')) {
    return 'Please upload a JPG, PNG, WEBP, or GIF image.';
  }

  return msg;
};

function SpecialistsAdminPage({ admin }) {
  const [specialists, setSpecialists] = useState(DEFAULT_SPECIALISTS);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);
  const [formMode, setFormMode] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(() => createSpecialistDraft());
  const [draftImageFile, setDraftImageFile] = useState(null);
  const [pendingDeleteSpecialist, setPendingDeleteSpecialist] = useState(null);
  const [isDeletingSpecialist, setIsDeletingSpecialist] = useState(false);
  const [toast, setToast] = useState(null);

  const load = async () => {
    setError('');
    try {
      const { data, error: queryError } = await fetchSpecialistRows({ includeInactive: true });
      if (queryError) {
        throw queryError;
      }
      setSpecialists(mergeSpecialistsWithRows(data ?? []));
    } catch (err) {
      setError(formatSpecialistImageError(err));
      setSpecialists(DEFAULT_SPECIALISTS);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    if (!toast) {
      return undefined;
    }
    const timer = setTimeout(() => setToast(null), 2600);
    return () => clearTimeout(timer);
  }, [toast]);

  const resetForm = () => {
    setFormMode(null);
    setEditingId(null);
    setDraft(createSpecialistDraft());
    setDraftImageFile(null);
  };

  const nextSortOrder = useMemo(() => {
    const highest = specialists.reduce(
      (max, item) => Math.max(max, Number(item.sortOrder) || 0),
      0
    );
    return highest + 10;
  }, [specialists]);

  const openAddForm = () => {
    setError('');
    setFormMode('add');
    setEditingId(null);
    setDraft(createSpecialistDraft({ sortOrder: nextSortOrder }));
    setDraftImageFile(null);
  };

  const openEditForm = (specialist) => {
    setError('');
    setFormMode('edit');
    setEditingId(specialist.id);
    setDraft(createSpecialistDraft(specialist));
    setDraftImageFile(null);
  };

  const validateSpecialistImageFile = (file) => {
    if (!file) {
      return '';
    }

    const lowerType = (file.type || '').toLowerCase();
    if (
      lowerType &&
      !['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(lowerType)
    ) {
      return 'Please upload a JPG, PNG, WEBP, or GIF image.';
    }
    if (file.size > SPECIALIST_IMAGE_MAX_BYTES) {
      return 'Please upload an image smaller than 5 MB.';
    }

    return '';
  };

  const uploadSpecialistImageAsset = async (specialistId, file) => {
    const validationError = validateSpecialistImageFile(file);
    if (validationError) {
      throw new Error(validationError);
    }

    const imagePath = buildSpecialistImagePath(specialistId, file.name);
    const { error: uploadError } = await getSupabase().storage
      .from(SPECIALIST_IMAGE_BUCKET)
      .upload(imagePath, file, {
        upsert: true,
        cacheControl: '3600',
        contentType: file.type || undefined
      });

    if (uploadError) {
      throw uploadError;
    }

    return imagePath;
  };

  const saveSpecialist = async () => {
    const trimmedName = draft.name.trim();
    if (!trimmedName) {
      setError('Please enter the healer name.');
      return;
    }

    const targetId =
      formMode === 'edit' && editingId ? editingId : slugifySpecialistId(draft.id || trimmedName);
    if (
      formMode === 'add' &&
      specialists.some((item) => item.id.toLowerCase() === targetId.toLowerCase())
    ) {
      setError('A healer with that generated ID already exists. Change the name slightly and try again.');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      const existing = specialists.find((item) => item.id === editingId) || {};
      const imagePath = draftImageFile
        ? await uploadSpecialistImageAsset(targetId, draftImageFile)
        : existing.imagePath || null;
      const { error: saveError } = await getSupabase().from('specialists').upsert(
        {
          id: targetId,
          name: trimmedName,
          role: draft.role.trim(),
          specialization: draft.specialization.trim(),
          experience: draft.experience.trim(),
          languages: draft.languages.trim(),
          bio: draft.bio.trim(),
          image_path: imagePath,
          image_position: existing.imagePosition || 'center center',
          sort_order: Number(draft.sortOrder) || nextSortOrder,
          is_active: true,
          updated_by: admin.email,
          updated_at: new Date().toISOString()
        },
        { onConflict: 'id' }
      );

      if (saveError) {
        throw saveError;
      }

      await load();
      resetForm();
      setToast({
        id: Date.now(),
        message:
          formMode === 'add'
            ? `${trimmedName} was added successfully.`
            : `${trimmedName} was updated successfully.`
      });
    } catch (err) {
      setError(formatSpecialistImageError(err));
    } finally {
      setIsSaving(false);
    }
  };

  const uploadSpecialistImage = async (specialist, file) => {
    if (!specialist || !file) {
      return;
    }

    setUploadingId(specialist.id);
    setError('');

    try {
      const imagePath = await uploadSpecialistImageAsset(specialist.id, file);

      const { error: saveError } = await getSupabase().from('specialists').upsert(
        {
          id: specialist.id,
          name: specialist.name,
          role: specialist.role,
          specialization: specialist.specialization,
          experience: specialist.experience,
          languages: specialist.languages,
          bio: specialist.bio,
          image_path: imagePath,
          image_position: specialist.imagePosition || 'center center',
          sort_order: Number(specialist.sortOrder) || nextSortOrder,
          is_active: true,
          updated_by: admin.email,
          updated_at: new Date().toISOString()
        },
        { onConflict: 'id' }
      );

      if (saveError) {
        throw saveError;
      }

      await load();
      setToast({
        id: Date.now(),
        message: `${specialist.name}'s image was updated successfully.`
      });
    } catch (err) {
      setError(formatSpecialistImageError(err));
    } finally {
      setUploadingId(null);
    }
  };

  const requestDeleteSpecialist = (specialist) => {
    if (!specialist || isDeletingSpecialist) {
      return;
    }
    setError('');
    setPendingDeleteSpecialist(specialist);
  };

  const cancelDeleteSpecialist = () => {
    if (isDeletingSpecialist) {
      return;
    }
    setPendingDeleteSpecialist(null);
  };

  const deleteSpecialist = async () => {
    if (!pendingDeleteSpecialist) {
      return;
    }

    setIsDeletingSpecialist(true);
    setError('');

    try {
      const supabase = getSupabase();
      const imagePath = pendingDeleteSpecialist.imagePath;

      const { error: deleteError } = await supabase
        .from('specialists')
        .delete()
        .eq('id', pendingDeleteSpecialist.id);

      if (deleteError) {
        throw deleteError;
      }

      if (imagePath) {
        const { error: storageError } = await supabase.storage
          .from(SPECIALIST_IMAGE_BUCKET)
          .remove([imagePath]);
        if (storageError) {
          console.warn('Could not remove specialist image from storage:', storageError.message);
        }
      }

      const deletedName = pendingDeleteSpecialist.name;
      setPendingDeleteSpecialist(null);
      await load();
      setToast({
        id: Date.now(),
        message: `${deletedName} was deleted successfully.`
      });
    } catch (err) {
      setError(formatSpecialistImageError(err));
    } finally {
      setIsDeletingSpecialist(false);
    }
  };

  return (
    <div className="admin-page">
      <PageHeader
        title="Specialists"
        subtitle="Add healers, edit their text, and replace the images shown on the public specialists page."
        onReload={load}
      />

      <section className="admin-panel">
        <header className="admin-panel__header">
          <div>
            <h3>Manage specialists</h3>
            <p>
              Update healer text, upload profile images, or add brand new healers. Files are stored in the Supabase bucket
              <code> {SPECIALIST_IMAGE_BUCKET} </code>
              and the specialist record is saved in the <code>specialists</code> table.
            </p>
          </div>
          <button
            type="button"
            className="admin-hero__btn admin-hero__btn--primary"
            onClick={openAddForm}
          >
            Add healer
          </button>
        </header>

        {error && <p className="auth-error">{error}</p>}
        {isLoading && <p className="admin-status">Loading specialists...</p>}

        <div className="admin-specialists-grid">
          {specialists.map((specialist) => {
            const isUploading = uploadingId === specialist.id;
            return (
              <article key={specialist.id} className="admin-specialist-card">
                <div className="admin-specialist-card__media">
                  <img
                    src={specialist.imageSrc}
                    alt={specialist.name}
                    className="admin-specialist-card__image"
                    style={{ objectPosition: specialist.imagePosition }}
                  />
                </div>
                <div className="admin-specialist-card__body">
                  <div>
                    <h3>{specialist.name}</h3>
                    <p className="admin-specialist-card__role">{specialist.role}</p>
                  </div>
                  <p className="admin-specialist-card__meta">
                    {specialist.imagePath
                      ? `Custom image saved at ${specialist.imagePath}`
                      : 'Using the default app image right now.'}
                  </p>
                  <p className="admin-specialist-card__blurb">
                    {truncate(specialist.bio || specialist.specialization, 150) || 'No description yet.'}
                  </p>
                  <p className="admin-specialist-card__hint">
                    {specialist.updatedAt
                      ? `Last updated ${formatDateTime(specialist.updatedAt)}`
                      : 'Upload JPG, PNG, WEBP, or GIF up to 5 MB.'}
                  </p>
                  <div className="admin-specialist-card__actions">
                    <button
                      type="button"
                      className="admin-hero__btn"
                      onClick={() => openEditForm(specialist)}
                    >
                      Edit text
                    </button>
                    <button
                      type="button"
                      className="admin-hero__btn admin-hero__btn--danger"
                      onClick={() => requestDeleteSpecialist(specialist)}
                      disabled={isDeletingSpecialist}
                    >
                      Delete
                    </button>
                    <label
                      className={`admin-hero__btn admin-hero__btn--primary admin-specialist-card__upload ${
                        isUploading ? 'is-disabled' : ''
                      }`}
                    >
                      <input
                        type="file"
                        accept={SPECIALIST_IMAGE_ACCEPT}
                        className="admin-specialist-card__file"
                        disabled={isUploading}
                        onChange={(event) => {
                          const nextFile = event.target.files?.[0];
                          void uploadSpecialistImage(specialist, nextFile);
                          event.target.value = '';
                        }}
                      />
                      {isUploading ? 'Uploading...' : 'Upload new image'}
                    </label>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {formMode && (
        <div className="admin-modal-backdrop" role="presentation" onClick={resetForm}>
          <div
            className="admin-modal"
            role="dialog"
            aria-modal="true"
            aria-label={formMode === 'add' ? 'Add healer' : 'Edit healer'}
            onClick={(event) => event.stopPropagation()}
          >
            <header className="admin-modal__header">
              <div>
                <h3>{formMode === 'add' ? 'Add healer' : 'Edit healer'}</h3>
                <p>
                  {formMode === 'add'
                    ? 'Create a new public specialist card and optionally upload the image now.'
                    : 'Update this healer’s public text details and optionally replace the image now.'}
                </p>
              </div>
              <button
                type="button"
                className="admin-modal__close"
                aria-label="Close"
                onClick={resetForm}
              >
                ×
              </button>
            </header>

            <div className="admin-specialist-form">
              <label className="auth-label" htmlFor="specialist-name">
                Name
              </label>
              <input
                id="specialist-name"
                type="text"
                className="auth-input"
                value={draft.name}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, name: event.target.value }))
                }
                placeholder="Healer name"
              />

              <p className="admin-specialist-form__hint">
                Public ID:{' '}
                <strong>
                  {formMode === 'edit' && editingId ? editingId : slugifySpecialistId(draft.id || draft.name)}
                </strong>
              </p>

              <div className="admin-specialist-form__grid">
                <div>
                  <label className="auth-label" htmlFor="specialist-role">
                    Role
                  </label>
                  <input
                    id="specialist-role"
                    type="text"
                    className="auth-input"
                    value={draft.role}
                    onChange={(event) =>
                      setDraft((current) => ({ ...current, role: event.target.value }))
                    }
                    placeholder="Reiki Master"
                  />
                </div>

                <div>
                  <label className="auth-label" htmlFor="specialist-experience">
                    Experience
                  </label>
                  <input
                    id="specialist-experience"
                    type="text"
                    className="auth-input"
                    value={draft.experience}
                    onChange={(event) =>
                      setDraft((current) => ({ ...current, experience: event.target.value }))
                    }
                    placeholder="10 years"
                  />
                </div>

                <div>
                  <label className="auth-label" htmlFor="specialist-languages">
                    Languages
                  </label>
                  <input
                    id="specialist-languages"
                    type="text"
                    className="auth-input"
                    value={draft.languages}
                    onChange={(event) =>
                      setDraft((current) => ({ ...current, languages: event.target.value }))
                    }
                    placeholder="English, Arabic"
                  />
                </div>

                <div>
                  <label className="auth-label" htmlFor="specialist-sort-order">
                    Sort order
                  </label>
                  <input
                    id="specialist-sort-order"
                    type="number"
                    className="auth-input"
                    value={draft.sortOrder}
                    onChange={(event) =>
                      setDraft((current) => ({ ...current, sortOrder: event.target.value }))
                    }
                    placeholder="40"
                  />
                </div>
              </div>

              <label className="auth-label" htmlFor="specialist-image-file">
                Image
              </label>
              <label className="admin-specialist-form__upload">
                <input
                  id="specialist-image-file"
                  type="file"
                  accept={SPECIALIST_IMAGE_ACCEPT}
                  className="admin-specialist-card__file"
                  onChange={(event) => {
                    const nextFile = event.target.files?.[0] || null;
                    setDraftImageFile(nextFile);
                    if (error) {
                      setError('');
                    }
                  }}
                />
                <span>{draftImageFile ? 'Change selected image' : 'Choose image to upload'}</span>
              </label>
              <p className="admin-specialist-form__hint">
                {draftImageFile
                  ? `Selected: ${draftImageFile.name}`
                  : formMode === 'edit'
                    ? 'Leave empty to keep the current image.'
                    : 'Optional. You can add a healer without an image and upload one later.'}
              </p>

              <label className="auth-label" htmlFor="specialist-specialization">
                Specialization
              </label>
              <textarea
                id="specialist-specialization"
                className="admin-textarea"
                rows={4}
                value={draft.specialization}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, specialization: event.target.value }))
                }
                placeholder="Energy healing, meditation, life guidance..."
              />

              <label className="auth-label" htmlFor="specialist-bio">
                Bio
              </label>
              <textarea
                id="specialist-bio"
                className="admin-textarea admin-modal__textarea"
                rows={7}
                value={draft.bio}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, bio: event.target.value }))
                }
                placeholder="Short public profile for this healer..."
              />
            </div>

            <div className="admin-modal__footer">
              <button type="button" className="admin-hero__btn" onClick={resetForm}>
                Cancel
              </button>
              <button
                type="button"
                className="admin-hero__btn admin-hero__btn--primary"
                onClick={() => void saveSpecialist()}
                disabled={isSaving}
              >
                {isSaving
                  ? 'Saving...'
                  : formMode === 'add'
                    ? 'Add healer'
                    : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingDeleteSpecialist && (
        <div
          className="admin-modal-backdrop"
          role="presentation"
          onClick={cancelDeleteSpecialist}
        >
          <div
            className="admin-modal admin-modal--confirm"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="specialist-delete-title"
            aria-describedby="specialist-delete-body"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="admin-confirm__icon" aria-hidden>
              <svg
                viewBox="0 0 24 24"
                width="28"
                height="28"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 6h18" />
                <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                <path d="M10 11v6" />
                <path d="M14 11v6" />
              </svg>
            </div>
            <h3 id="specialist-delete-title" className="admin-confirm__title">
              Delete this specialist?
            </h3>
            <p id="specialist-delete-body" className="admin-confirm__body">
              This healer will be removed from the public specialists page and from the admin list.
            </p>
            <blockquote className="admin-confirm__quote">
              {pendingDeleteSpecialist.name}
              {'\n\n'}
              {pendingDeleteSpecialist.role || 'No role set'}
            </blockquote>
            <div className="admin-modal__footer">
              <button
                type="button"
                className="admin-hero__btn"
                onClick={cancelDeleteSpecialist}
                disabled={isDeletingSpecialist}
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-hero__btn admin-hero__btn--danger"
                onClick={() => void deleteSpecialist()}
                disabled={isDeletingSpecialist}
              >
                {isDeletingSpecialist ? 'Deleting...' : 'Yes, delete it'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="admin-toast" role="status" aria-live="polite" key={toast.id}>
          <span className="admin-toast__icon" aria-hidden>
            <svg
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M5 12.5l4.5 4.5L19 7" />
            </svg>
          </span>
          <span className="admin-toast__message">{toast.message}</span>
          <button
            type="button"
            className="admin-toast__close"
            aria-label="Dismiss"
            onClick={() => setToast(null)}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}

function LearningsPage() {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState('');
  const [newName, setNewName] = useState('');
  const [newText, setNewText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [editingText, setEditingText] = useState('');
  const [actionError, setActionError] = useState('');
  const [pendingDelete, setPendingDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toast, setToast] = useState(null);

  const normalizeLearningName = (value) => {
    const trimmed = value.trim();
    return trimmed || null;
  };

  const formatLearningActionError = (err) => {
    const msg = err instanceof Error ? err.message : String(err);
    if (
      msg.toLowerCase().includes('chat_learnings') &&
      msg.toLowerCase().includes('name')
    ) {
      return 'Learning name column not found. Run the new SQL migration first.';
    }
    return msg;
  };

  const load = async () => {
    setError('');
    try {
      const { data, error: queryError } = await getSupabase()
        .from('chat_learnings')
        .select('id, name, content, created_at')
        .order('created_at', { ascending: false })
        .limit(500);
      if (queryError) throw queryError;
      setRows(data ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setRows([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const showToast = (message, kind = 'success') => {
    setToast({ id: Date.now(), message, kind });
  };

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 2600);
    return () => clearTimeout(timer);
  }, [toast]);

  const addLearning = async () => {
    const trimmed = newText.trim();
    if (!trimmed) return;
    setIsSaving(true);
    setActionError('');
    try {
      const { error: queryError } = await getSupabase()
        .from('chat_learnings')
        .insert({
          name: normalizeLearningName(newName),
          content: trimmed
        });
      if (queryError) throw queryError;
      setNewName('');
      setNewText('');
      await load();
      showToast('Learning saved. The assistant will start applying it now.');
    } catch (err) {
      setActionError(formatLearningActionError(err));
    } finally {
      setIsSaving(false);
    }
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setEditingName(row.name || '');
    setEditingText(row.content);
    setActionError('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingName('');
    setEditingText('');
  };

  useEffect(() => {
    if (editingId === null && pendingDelete === null) return undefined;
    const onKey = (event) => {
      if (event.key !== 'Escape') return;
      if (pendingDelete) {
        cancelDelete();
      } else if (editingId !== null) {
        cancelEdit();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingId, pendingDelete, isDeleting]);

  const saveEdit = async () => {
    const trimmed = editingText.trim();
    if (!trimmed || editingId == null) return;
    setActionError('');
    try {
      const { data, error: queryError } = await getSupabase()
        .from('chat_learnings')
        .update({
          name: normalizeLearningName(editingName),
          content: trimmed
        })
        .eq('id', editingId)
        .select('id');
      if (queryError) throw queryError;
      if (!data || data.length === 0) {
        throw new Error(
          'Update was blocked by Supabase row-level security. Run the migration "20260509190000_chat_learnings_admin_policies.sql" to allow admin updates.'
        );
      }
      cancelEdit();
      await load();
      showToast('Learning updated.');
    } catch (err) {
      setActionError(formatLearningActionError(err));
    }
  };

  const requestDelete = (row) => {
    setActionError('');
    setPendingDelete(row);
  };

  const cancelDelete = () => {
    if (isDeleting) return;
    setPendingDelete(null);
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setIsDeleting(true);
    setActionError('');
    try {
      const { data, error: queryError } = await getSupabase()
        .from('chat_learnings')
        .delete()
        .eq('id', pendingDelete.id)
        .select('id');
      if (queryError) throw queryError;
      if (!data || data.length === 0) {
        throw new Error(
          'Delete was blocked by Supabase row-level security. Run the migration "20260509190000_chat_learnings_admin_policies.sql" to allow admin deletes.'
        );
      }
      setPendingDelete(null);
      await load();
      showToast('Learning deleted. It will no longer be applied to replies.');
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="admin-page">
      <PageHeader
        title="Learnings"
        subtitle="You decide both how the bot speaks AND what it knows. Behavioral rules and factual knowledge are auto-injected into every reply for all users."
        onReload={load}
      />

      <section className="admin-panel">
        <header className="admin-panel__header">
          <div>
            <h3>Add a new learning</h3>
            <p>
              Write a behavior rule <em>or</em> a piece of factual information —
              in any language. The assistant will understand it, treat facts as
              ground truth (overriding its prior knowledge), follow rules
              exactly, and answer in the user's own language from the next
              message onward.
            </p>
            <ul className="admin-help-list">
              <li>
                <strong>Topic knowledge:</strong> "Depression is a mood
                disorder marked by persistent low mood, loss of interest, low
                energy, and sleep changes. Helpful first steps: gentle daily
                routine, sunlight, light movement, talking to a trusted person,
                and reaching out to a professional if symptoms last more than
                two weeks. Encourage seeking professional support; never
                diagnose."
              </li>
              <li>
                <strong>How-to / advice:</strong> "How to overcome anxiety:
                slow nasal breathing 4-in / 6-out for 90 seconds; name 5 things
                you can see; gentle movement; reduce caffeine; journal one
                worry and one tiny next step."
              </li>
              <li>
                <strong>Behavior:</strong> "Reply in 2-3 short sentences. Never
                use emojis. Always end with a calm, grounding question."
              </li>
              <li>
                <strong>Facts / data:</strong> "Our app's daily reflection
                runs at 7am local time and is free for all users."
              </li>
              <li>
                <strong>FAQ:</strong> "Q: Are sessions confidential? A: Yes —
                conversations are private to the user account."
              </li>
            </ul>
          </div>
        </header>
        <label className="auth-label" htmlFor="learning-name">
          Learning name
        </label>
        <input
          id="learning-name"
          type="text"
          className="auth-input"
          value={newName}
          onChange={(event) => setNewName(event.target.value)}
          placeholder="Optional short title, e.g. Anxiety guide"
          maxLength={120}
        />
        <label className="auth-label" htmlFor="learning-content">
          Learning content
        </label>
        <textarea
          id="learning-content"
          className="admin-textarea"
          rows={4}
          value={newText}
          onChange={(event) => setNewText(event.target.value)}
          placeholder="Type a rule, a fact, an explanation, or how-to advice — e.g. 'What is burnout: a state of exhaustion from prolonged stress…' or 'Always reply in 2-3 short bullet points. No emojis.' (any language is fine)"
        />
        <div className="admin-actions">
          <button
            type="button"
            className="admin-hero__btn admin-hero__btn--primary"
            disabled={isSaving || !newText.trim()}
            onClick={() => void addLearning()}
          >
            {isSaving ? 'Saving…' : 'Save learning'}
          </button>
          {newText && (
            <button
              type="button"
              className="admin-hero__btn"
              onClick={() => {
                setNewName('');
                setNewText('');
              }}
            >
              Clear
            </button>
          )}
          <span className="admin-status">
            {rows ? `${rows.length} note${rows.length === 1 ? '' : 's'} active` : ''}
          </span>
        </div>
        {actionError && <p className="auth-error">{actionError}</p>}
      </section>

      <section className="admin-panel">
        <header className="admin-panel__header">
          <div>
            <h3>All learnings</h3>
            <p>Edit or remove notes the assistant follows.</p>
          </div>
        </header>
        {error && <p className="auth-error">{error}</p>}
        {rows === null && <p className="admin-status">Loading…</p>}
        {rows && rows.length === 0 && (
          <p className="admin-status">
            No learnings yet — add the first one above.
          </p>
        )}
        {rows && rows.length > 0 && (
          <div className="admin-table-wrap">
            <table className="admin-table admin-table--learnings">
              <thead>
                <tr>
                  <th style={{ width: '14rem' }}>Name</th>
                  <th>Preview</th>
                  <th style={{ width: '8rem' }}>Created</th>
                  <th style={{ width: '11rem' }} className="admin-table__actions-col">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td title={row.name || 'Untitled learning'}>
                      {row.name?.trim() || '—'}
                    </td>
                    <td title={row.content}>{truncate(row.content, 90)}</td>
                    <td title={formatDateTime(row.created_at)}>
                      {formatRelative(row.created_at)}
                    </td>
                    <td className="admin-table__actions-col">
                      <div className="admin-table__actions">
                        <button
                          type="button"
                          className="admin-hero__btn"
                          onClick={() => startEdit(row)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="admin-hero__btn admin-hero__btn--danger"
                          onClick={() => requestDelete(row)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {pendingDelete && (
        <div
          className="admin-modal-backdrop"
          role="presentation"
          onClick={cancelDelete}
        >
          <div
            className="admin-modal admin-modal--confirm"
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="admin-confirm-title"
            aria-describedby="admin-confirm-body"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="admin-confirm__icon" aria-hidden>
              <svg
                viewBox="0 0 24 24"
                width="28"
                height="28"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 6h18" />
                <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                <path d="M10 11v6" />
                <path d="M14 11v6" />
              </svg>
            </div>
            <h3 id="admin-confirm-title" className="admin-confirm__title">
              Delete this learning?
            </h3>
            <p id="admin-confirm-body" className="admin-confirm__body">
              The note below will be permanently removed and the assistant will
              stop applying it on the very next reply.
            </p>
            <blockquote className="admin-confirm__quote">
              {pendingDelete.name?.trim()
                ? `${pendingDelete.name.trim()}\n\n${truncate(pendingDelete.content, 240)}`
                : truncate(pendingDelete.content, 240)}
            </blockquote>
            {actionError && <p className="auth-error">{actionError}</p>}
            <div className="admin-modal__footer">
              <button
                type="button"
                className="admin-hero__btn"
                onClick={cancelDelete}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-hero__btn admin-hero__btn--danger"
                onClick={() => void confirmDelete()}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting…' : 'Yes, delete it'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div
          className={`admin-toast admin-toast--${toast.kind}`}
          role="status"
          aria-live="polite"
          key={toast.id}
        >
          <span className="admin-toast__icon" aria-hidden>
            <svg
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M5 12.5l4.5 4.5L19 7" />
            </svg>
          </span>
          <span className="admin-toast__message">{toast.message}</span>
          <button
            type="button"
            className="admin-toast__close"
            aria-label="Dismiss"
            onClick={() => setToast(null)}
          >
            ×
          </button>
        </div>
      )}

      {editingId !== null && (
        <div
          className="admin-modal-backdrop"
          role="presentation"
          onClick={cancelEdit}
        >
          <div
            className="admin-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Edit learning"
            onClick={(event) => event.stopPropagation()}
          >
            <header className="admin-modal__header">
              <div>
                <h3>Edit learning</h3>
                <p>The full note is shown below — change it however you'd like.</p>
              </div>
              <button
                type="button"
                className="admin-modal__close"
                aria-label="Close"
                onClick={cancelEdit}
              >
                ×
              </button>
            </header>
            <label className="auth-label" htmlFor="edit-learning-name">
              Learning name
            </label>
            <input
              id="edit-learning-name"
              type="text"
              className="auth-input"
              value={editingName}
              onChange={(event) => setEditingName(event.target.value)}
              placeholder="Optional short title"
              maxLength={120}
            />
            <label className="auth-label" htmlFor="edit-learning-content">
              Learning content
            </label>
            <textarea
              id="edit-learning-content"
              className="admin-textarea admin-modal__textarea"
              rows={10}
              value={editingText}
              onChange={(event) => setEditingText(event.target.value)}
              autoFocus
            />
            {actionError && <p className="auth-error">{actionError}</p>}
            <div className="admin-modal__footer">
              <button
                type="button"
                className="admin-hero__btn"
                onClick={cancelEdit}
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-hero__btn admin-hero__btn--primary"
                onClick={() => void saveEdit()}
                disabled={!editingText.trim()}
              >
                Save changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MemoriesPage() {
  const { rows, error, reload } = useSupabaseRows({
    table: 'user_memories',
    select: 'id, user_id, kind, content, source_session_id, created_at',
    order: 'created_at',
    limit: 200
  });

  return (
    <div className="admin-page">
      <PageHeader
        title="User memories"
        subtitle="Per-user continuity notes used across all of a user's chats."
        onReload={reload}
      />
      <StatusLine rows={rows} error={error} emptyText="No user memories yet." />
      {rows && rows.length > 0 && (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>User</th>
                <th>Kind</th>
                <th>Content</th>
                <th>Source session</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>{row.id}</td>
                  <td>{row.user_id}</td>
                  <td>{row.kind}</td>
                  <td title={row.content}>{truncate(row.content, 140)}</td>
                  <td>{row.source_session_id ?? '—'}</td>
                  <td>{formatDateTime(row.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SettingsPage({ admin, onSignOut }) {
  return (
    <div className="admin-page">
      <PageHeader title="Settings" subtitle="Account & app shortcuts" />
      <div className="admin-card-block">
        <p className="section-label">Account</p>
        <p>
          <strong>{admin.name}</strong>
          <br />
          {admin.email}
        </p>
      </div>
      <div className="admin-card-block">
        <p className="section-label">Shortcuts</p>
        <div className="admin-actions">
          <a className="journey-button" href="/">
            Open user app
          </a>
          <button type="button" className="new-session-button" onClick={onSignOut}>
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

function AdminDashboard({ admin, onSignOut }) {
  const [activeTab, setActiveTab] = useState(() => getTabFromPath());
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    const onPop = () => setActiveTab(getTabFromPath());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    const desired = pathForTab(activeTab);
    const current = window.location.pathname.replace(/\/+$/, '') || '/';
    const isNestedPath = current === desired || current.startsWith(`${desired}/`);
    if (!isNestedPath) {
      window.history.replaceState({}, '', desired);
    }
    if (typeof document !== 'undefined') {
      const tab = TABS.find((t) => t.id === activeTab);
      document.title = tab ? `Admin · ${tab.label}` : 'Admin';
    }
  }, [activeTab]);

  const navigateToTab = (tabId) => {
    if (!TAB_IDS.includes(tabId)) return;
    if (tabId === activeTab) {
      setMobileNavOpen(false);
      return;
    }
    window.history.pushState({}, '', pathForTab(tabId));
    setActiveTab(tabId);
    setMobileNavOpen(false);
  };

  const renderActivePage = () => {
    switch (activeTab) {
      case 'chat':
        return <AdminChatPage admin={admin} onSignOut={onSignOut} />;
      case 'specialists':
        return <SpecialistsAdminPage admin={admin} />;
      case 'users':
        return <UsersPage />;
      case 'sessions':
        return <SessionsPage />;
      case 'messages':
        return <MessagesPage />;
      case 'bookings':
        return <BookingsPage />;
      case 'learnings':
        return <LearningsPage />;
      case 'memories':
        return <MemoriesPage />;
      case 'settings':
        return <SettingsPage admin={admin} onSignOut={onSignOut} />;
      case 'overview':
      default:
        return <OverviewPage admin={admin} onNavigate={navigateToTab} />;
    }
  };

  const activeLabel = TABS.find((t) => t.id === activeTab)?.label ?? 'Overview';

  return (
    <div className={`admin-dashboard ${mobileNavOpen ? 'is-mobile-open' : ''}`}>
      <aside className="admin-dashboard__sidebar">
        <div className="admin-dashboard__brand">
          <p className="section-label">Admin</p>
          <h1>ChatAI</h1>
          <p className="admin-dashboard__user">
            {admin.name}
            <br />
            <span>{admin.email}</span>
          </p>
        </div>

        <div className="admin-mobile-shortcuts">
          <button
            type="button"
            className={`admin-mobile-shortcut ${activeTab === 'chat' ? 'active' : ''}`}
            onClick={() => navigateToTab('chat')}
          >
            <span className="admin-mobile-shortcut__icon" aria-hidden>
              {ICONS.chat}
            </span>
            <span>Chat</span>
          </button>
        </div>

        <nav className="admin-dashboard__nav" aria-label="Admin sections">
          {SIDEBAR_TABS.map((tab) => (
            <a
              key={tab.id}
              href={pathForTab(tab.id)}
              className={`admin-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={(event) => {
                if (
                  event.metaKey ||
                  event.ctrlKey ||
                  event.shiftKey ||
                  event.altKey ||
                  event.button === 1
                ) {
                  return;
                }
                event.preventDefault();
                navigateToTab(tab.id);
              }}
            >
              <span className="admin-tab__icon" aria-hidden>
                {tab.icon}
              </span>
              <span className="admin-tab__label">{tab.label}</span>
            </a>
          ))}
        </nav>

        <div className="admin-dashboard__sidebar-footer">
          <button type="button" className="new-session-button" onClick={onSignOut}>
            Sign out
          </button>
        </div>
      </aside>

      <main className="admin-dashboard__main">
        <div className="admin-dashboard__topbar">
          <button
            type="button"
            className="admin-mobile-toggle"
            aria-expanded={mobileNavOpen}
            onClick={() => setMobileNavOpen((v) => !v)}
          >
            Menu
          </button>
          <span className="admin-dashboard__crumb">
            Dashboard <span aria-hidden>›</span> {activeLabel}
          </span>
        </div>
        <div
          className={`admin-dashboard__content ${
            activeTab === 'chat' ? 'admin-dashboard__content--chat' : ''
          }`}
        >
          {renderActivePage()}
        </div>
      </main>

      <button
        type="button"
        className="admin-dashboard__overlay"
        aria-label="Close menu"
        tabIndex={mobileNavOpen ? 0 : -1}
        onClick={() => setMobileNavOpen(false)}
      />
    </div>
  );
}

export default function Admin() {
  const [admin, setAdmin] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const root = document.getElementById('root');
    document.documentElement.classList.add('admin-route-active');
    document.body.classList.add('admin-route-active');
    root?.classList.add('admin-route-active');
    return () => {
      document.documentElement.classList.remove('admin-route-active');
      document.body.classList.remove('admin-route-active');
      root?.classList.remove('admin-route-active');
    };
  }, []);

  useEffect(() => {
    const raw = window.localStorage.getItem(ADMIN_SESSION_KEY);
    let stored = null;
    try {
      stored = raw ? JSON.parse(raw) : null;
    } catch (_err) {
      window.localStorage.removeItem(ADMIN_SESSION_KEY);
    }

    const storedEmail =
      typeof stored?.email === 'string' ? stored.email.trim().toLowerCase() : '';
    if (storedEmail && storedEmail === ADMIN_EMAIL.toLowerCase()) {
      setAdmin({
        id: 'local-admin',
        name: ADMIN_NAME,
        email: ADMIN_EMAIL
      });
    } else if (stored) {
      window.localStorage.removeItem(ADMIN_SESSION_KEY);
    }
    setIsLoading(false);
  }, []);

  if (isLoading) {
    return (
      <div className="auth-shell">
        <div className="auth-card">
          <h1>Checking admin session...</h1>
        </div>
      </div>
    );
  }

  if (!admin) {
    return <AdminLogin onSignedIn={(data) => setAdmin(data)} />;
  }

  const signOut = () => {
    window.localStorage.removeItem(ADMIN_SESSION_KEY);
    setAdmin(null);
  };

  return <AdminDashboard admin={admin} onSignOut={signOut} />;
}
